import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  formatDate,
  formatDateTime,
  formatMoney,
  istanbulDayString,
  shiftIsoDay,
  t,
} from '@aquabox/shared';
import { useToast } from '../components/Toast';
import { ConfirmDialog } from '../components/ConfirmDialog';
import {
  isOwnerProfile,
  useApproveDevice,
  useDriverDevices,
  usePersonnelDrivers,
  usePersonnelLedger,
  usePersonnelLedgerDay,
  usePersonnelMutations,
  usePersonnelVehicles,
  useRevokeDevice,
  useUnlockDevice,
  type DriverDevice,
  type PersonnelDriver,
  type PersonnelLedgerDay,
  type PersonnelLedgerRange,
} from '../api/personnel';
import { useMe } from '../api/queries';
import { usePanelAssign, useSaveDriverMap } from '../api/panel-assign';
import { useShadowAssign } from '../api/shadow-assign';
import type { Vehicle } from '../api/types';
import { DriverPerformance } from './Drivers';
import { PerformanceOverview, PersonPerformance } from './PersonnelPerformance';
import { rateText } from './ShadowAssignCard';

/**
 * Personel ekranı (`/personel`).
 *
 * 21.10 kararı: "Ekip" (/ekip) ve "Şoförler" (/soforler) AYRI ekranlardan tek ekrana
 * toplandı. İki sekme var:
 *
 *  - **Kişiler** — ekleme, düzenleme, pasifleştirme, PIN, araç zimmeti, kişi detayı
 *    (Bilgiler / Defter / Cihazlar).
 *  - **Performans** — eski `/soforler` ekranının içeriği AYNEN (`DriverPerformance`).
 *
 * KURAL: para ve bakiye panelde HESAPLANMAZ. Defter rakamları sunucudan gelir
 * (`Decimal` → string); panel yalnız biçimlendirir (CLAUDE.md kural 4).
 */

/** Defter aralığı en fazla 31 gün (sunucu sınırı; panel de aynı sınırı bilerek uygular). */
const LEDGER_MAX_DAYS = 31;

const EMPTY_PERSON = { fullName: '', phone: '', vehicleId: '' };

/** "YYYY-MM-DD" iki gün arasındaki gün sayısı (iki uç dahil). */
function dayCount(from: string, to: string): number {
  const ms = Date.parse(`${to}T00:00:00.000Z`) - Date.parse(`${from}T00:00:00.000Z`);
  return Math.round(ms / 86_400_000) + 1;
}

/** Baskı başlığındaki tarih; aralık alanı boşsa (kullanıcı temizlediyse) çizgi. */
function printDate(iso: string): string {
  return iso === '' ? '—' : formatDate(iso);
}

/**
 * Sunucu hatasını anlaşılır Türkçe metne çevirir.
 *
 * Sözleşme kodları (`PHONE_TAIL_TAKEN`, `driver.phoneRequired`) panelin kendi metnini
 * kullanır; sunucunun mesajı da Türkçedir ama kod eşlemesi daha güvenilirdir.
 */
export function personnelErrorMessage(error: unknown): string {
  const err = error as
    | {
        status?: number;
        message?: string;
        body?: { code?: string; issues?: { path: string; message: string }[] };
      }
    | null
    | undefined;
  if (!err) return t('error.internal');
  const code = err.body?.code;
  if (code === 'PHONE_TAIL_TAKEN') return t('personnel.phoneTailTakenError');
  if (code === 'driver.phoneRequired') return t('personnel.phoneRequiredError');
  if (code === 'PROFILE_EXISTS') return t('driver.ownerProfileExists');
  if (code === 'OWNER_PROFILE_NO_PIN') return t('driver.ownerProfileNoPin');
  if (code === 'OWNER_PROFILE_USER_FIELDS') return t('driver.ownerProfileUserFields');
  const phoneIssue = err.body?.issues?.find((i) => i.path === 'phone');
  if (phoneIssue) return phoneIssue.message;
  if (err.message && err.message.length > 0) return err.message;
  return t('error.internal');
}

export function Personnel() {
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'performans' ? 'performans' : 'kisiler';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div
        className="no-print"
        style={{ display: 'flex', gap: 8, padding: '28px 32px 0' }}
        role="tablist"
        aria-label={t('personnel.title')}
      >
        <TabButton
          active={tab === 'kisiler'}
          onClick={() => setParams({})}
          testId="personnel-tab-people"
        >
          {t('personnel.tabPeople')}
        </TabButton>
        <TabButton
          active={tab === 'performans'}
          onClick={() => setParams({ tab: 'performans' })}
          testId="personnel-tab-performance"
        >
          {t('personnel.tabPerformance')}
        </TabButton>
      </div>

      {tab === 'performans' ? (
        <>
          <PerformanceOverview />
          <DriverPerformance />
          <PanelMatchByPerson />
        </>
      ) : (
        <PeopleTab />
      )}
    </div>
  );
}

/**
 * Panel uyumu — KİŞİ (şoför) bazında.
 *
 * Gölge atama (`GET /api/integrations/shadow-assign`) yanıtındaki `byPerson` karşılaştırmayı
 * PANEL tarafındaki kişiye göre gruplar. Oran SUNUCUDAN gelir (`ratePct`); panel hesap yapmaz
 * (CLAUDE.md kural 4). Veri yoksa "—" yazılır.
 *
 * NOT: `DriverPerformance` tablosu `screens/Drivers.tsx` içindedir ve bu görevin izin
 * listesinde değildir; bu yüzden kişi başına oran kendi tablosunda gösterilir.
 */
function PanelMatchByPerson() {
  const me = useMe();
  const canView = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  const shadow = useShadowAssign({ days: 30, poll: false, enabled: canView });
  const driversQ = usePersonnelDrivers();

  const byPerson = new Map(
    (shadow.data?.byPerson ?? []).flatMap((p) => (p.driver ? [[p.driver.id, p] as const] : [])),
  );
  const drivers = driversQ.data?.items ?? [];
  const th = {
    textAlign: 'left' as const,
    padding: '8px 12px',
    fontSize: 12.5,
    color: 'var(--text-secondary)',
    fontWeight: 600,
    borderBottom: '1px solid var(--border)',
  };
  const td = { padding: '8px 12px', fontSize: 13.5 };

  return (
    <section
      className="card"
      data-testid="shadow-person-table"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}
    >
      <strong style={{ fontSize: 14 }}>{t('panel.shadowAssign.personTableTitle')}</strong>
      <p style={{ margin: 0, fontSize: 12.5, color: 'var(--text-secondary)' }}>
        {t('panel.shadowAssign.personTableHint')}
      </p>
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <th style={th}>{t('panel.shadowAssign.colDriver')}</th>
            <th style={th}>{t('panel.shadowAssign.colPanelMatch')}</th>
          </tr>
        </thead>
        <tbody>
          {drivers.length === 0 && (
            <tr>
              <td colSpan={2} style={{ ...td, color: 'var(--text-secondary)' }}>
                {t('panel.shadowAssign.dash')}
              </td>
            </tr>
          )}
          {drivers.map((d) => {
            const m = byPerson.get(d.id);
            return (
              <tr key={d.id} data-testid={`shadow-person-row-${d.id}`}>
                <td style={td}>{d.user.fullName}</td>
                <td style={td} data-testid={`shadow-person-rate-${d.id}`}>
                  {m
                    ? `${rateText(m.d7.ratePct)} / ${rateText(m.d30.ratePct)}`
                    : t('panel.shadowAssign.dash')}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </section>
  );
}

function TabButton({
  active,
  onClick,
  children,
  testId,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
  testId: string;
}) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      data-testid={testId}
      onClick={onClick}
      style={{
        borderRadius: 'var(--r-pill)',
        padding: '10px 15px',
        fontWeight: 600,
        fontSize: 13,
        background: active ? 'var(--primary)' : 'var(--surface)',
        border: `1px solid ${active ? 'var(--primary)' : 'var(--border)'}`,
        color: active ? '#fff' : 'var(--text-secondary)',
      }}
    >
      {children}
    </button>
  );
}

/** "Kişiler" sekmesi: liste + ekleme formu + detay paneli. */
function PeopleTab() {
  const toast = useToast();
  const me = useMe();
  const driversQ = usePersonnelDrivers();
  const vehiclesQ = usePersonnelVehicles();
  const { create, update, resetPin, deactivate, openOwnerProfile, assignVehicle } =
    usePersonnelMutations();
  const [draft, setDraft] = useState<typeof EMPTY_PERSON | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  /** Liste işlemlerinin (PIN, pasife alma, aktifleştirme, zimmet, patron profili) hatası: formdan bağımsız. */
  const [listError, setListError] = useState<string | null>(null);
  /** Üretilen PIN yalnız BİR KEZ döner; kart kapanınca bir daha gösterilemez. */
  const [pinCard, setPinCard] = useState<{ name: string; pin: string } | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  /** Yıkıcı işlem onayı bekleyen personel: PIN sıfırla ya da pasifleştir. */
  const [confirm, setConfirm] = useState<{
    kind: 'pin' | 'retire';
    driver: PersonnelDriver;
  } | null>(null);

  const drivers = driversQ.data?.items ?? [];
  const vehicles = vehiclesQ.data?.items ?? [];
  const selected = drivers.find((d) => d.id === selectedId) ?? null;
  const ownerProfileExists = drivers.some(isOwnerProfile);
  const isOwner = me.data?.role === 'OWNER';
  /** Telefonu olmayan satırda açık telefon kutusu (aynı anda tek satır). */
  const [phoneEdit, setPhoneEdit] = useState<{
    id: string;
    value: string;
    error: string | null;
  } | null>(null);

  function savePhone(d: PersonnelDriver): void {
    if (!phoneEdit || phoneEdit.id !== d.id) return;
    const phone = phoneEdit.value.trim();
    // Telefon ZORUNLU: boş değer sunucuya gitmez (detaydaki Bilgiler kaydıyla aynı kural).
    if (phone === '') {
      setPhoneEdit({ ...phoneEdit, error: t('personnel.phoneRequiredError') });
      return;
    }
    // Detaydaki telefon kaydıyla AYNI mutation; yalnız telefon gönderilir (vehicleId dokunulmaz).
    update.mutate(
      { id: d.id, phone },
      {
        onSuccess: () => {
          // Yalnız bu satırın kutusu kapanır; başka satırın açık kutusuna dokunulmaz.
          setPhoneEdit((cur) => (cur?.id === d.id ? null : cur));
          setListError(null);
          toast(t('personnel.infoSaved'));
        },
        onError: (e) =>
          setPhoneEdit((cur) =>
            cur && cur.id === d.id ? { ...cur, error: personnelErrorMessage(e) } : cur,
          ),
      },
    );
  }

  function submitNew(): void {
    if (!draft) return;
    const fullName = draft.fullName.trim();
    const phone = draft.phone.trim();
    if (fullName.length < 2) return;
    // Telefonu yalnız OWNER yazar (MANAGER'da alan yok, sunucu 403 `PHONE_OWNER_ONLY`) ve OWNER için
    // zorunludur: boş telefon sunucuya GÖNDERİLMEZ (panel Türkçe nedeni daha erken gösterir).
    if (isOwner && phone === '') {
      setFormError(t('personnel.phoneRequiredError'));
      return;
    }
    setFormError(null);
    create.mutate(
      {
        fullName,
        ...(isOwner ? { phone } : {}),
        vehicleId: draft.vehicleId === '' ? undefined : draft.vehicleId,
      },
      {
        onSuccess: (d) => {
          setDraft(null);
          setPinCard({ name: d.user.fullName, pin: d.pin });
        },
        onError: (e) => setFormError(personnelErrorMessage(e)),
      },
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18, padding: '0 32px 36px' }}>
      <section className="no-print" style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <h2 className="h2" style={{ margin: 0 }}>
            {t('personnel.tabPeople')}
          </h2>
          <span style={{ flex: 1 }} />
          {!ownerProfileExists && isOwner ? (
            <button
              className="btn"
              data-testid="personnel-open-owner-profile"
              onClick={() =>
                openOwnerProfile.mutate(
                  {},
                  {
                    onSuccess: () => {
                      setListError(null);
                      toast(t('personnel.ownerProfileOpened'));
                    },
                    onError: (e) => setListError(personnelErrorMessage(e)),
                  },
                )
              }
            >
              {t('personnel.openOwnerProfile')}
            </button>
          ) : null}
          <button
            className="btn btn-primary"
            data-testid="personnel-add-person"
            // `me` gelmeden rol bilinmez: form MANAGER'a telefon alanıyla açılmasın.
            disabled={me.isPending}
            onClick={() => {
              setFormError(null);
              setDraft({ ...EMPTY_PERSON });
            }}
          >
            {t('personnel.addPerson')}
          </button>
        </div>

        <p style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6, margin: 0 }}>
          {t('personnel.peopleHint')}
        </p>

        {listError ? (
          <p
            role="alert"
            data-testid="personnel-list-error"
            style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}
          >
            {listError}
          </p>
        ) : null}

        {pinCard ? (
          <PinCard
            name={pinCard.name}
            pin={pinCard.pin}
            onClose={() => setPinCard(null)}
            onCopied={() => toast(t('fleet.pinCopied'))}
          />
        ) : null}

        {draft ? (
          <div
            className="card"
            data-testid="personnel-add-form"
            style={{
              padding: 16,
              borderColor: 'var(--primary)',
              display: 'flex',
              flexDirection: 'column',
              gap: 10,
            }}
          >
            <div className="form-grid" style={{ '--form-gap': '10px' } as React.CSSProperties}>
              <input
                value={draft.fullName}
                onChange={(e) => setDraft({ ...draft, fullName: e.target.value })}
                placeholder={t('personnel.infoFullName')}
                aria-label={t('personnel.infoFullName')}
                style={{ height: 44 }}
              />
              {isOwner ? (
                <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <span
                    style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--text-secondary)' }}
                    data-testid="personnel-phone-required-mark"
                  >
                    {t('personnel.phoneLabel')}
                  </span>
                  <input
                    value={draft.phone}
                    onChange={(e) => setDraft({ ...draft, phone: e.target.value })}
                    placeholder={t('fleet.phonePlaceholder')}
                    inputMode="tel"
                    aria-label={t('personnel.infoPhone')}
                    aria-required="true"
                    data-testid="personnel-phone-input"
                    style={{ height: 44 }}
                  />
                </label>
              ) : null}
            </div>
            <select
              value={draft.vehicleId}
              onChange={(e) => setDraft({ ...draft, vehicleId: e.target.value })}
              aria-label={t('personnel.infoVehicle')}
              style={{ height: 44 }}
            >
              <option value="">{t('fleet.noVehicleYet')}</option>
              {vehicles.map((v) => (
                <option key={v.id} value={v.id}>
                  {v.plate}
                  {v.label ? ` · ${v.label}` : ''}
                </option>
              ))}
            </select>
            {isOwner ? (
              <p
                style={{
                  fontSize: 12.5,
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5,
                  margin: 0,
                }}
              >
                {t('personnel.phoneRequiredHint')}
              </p>
            ) : null}
            {formError ? (
              <p
                data-testid="personnel-form-error"
                style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}
              >
                {formError}
              </p>
            ) : null}
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn" onClick={() => setDraft(null)}>
                {t('common.cancel')}
              </button>
              <button
                className="btn btn-primary"
                data-testid="personnel-save-person"
                disabled={
                  draft.fullName.trim().length < 2 ||
                  (isOwner && draft.phone.trim() === '') ||
                  create.isPending
                }
                onClick={submitNew}
              >
                {t('personnel.savePerson')}
              </button>
            </div>
          </div>
        ) : null}

        <div
          className="card"
          style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 4 }}
        >
          {drivers.length === 0 ? (
            <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('personnel.noPeople')}
            </p>
          ) : null}

          {drivers.map((d) => (
            <div
              key={d.id}
              className="rowgrid-stack"
              data-testid={`personnel-row-${d.id}`}
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 130px 92px 92px',
                alignItems: 'center',
                gap: 10,
                minHeight: 62,
                borderTop: '1px solid var(--border)',
                opacity: d.isActive ? 1 : 0.55,
              }}
            >
              <span style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <button
                  type="button"
                  className="btn-link"
                  data-testid={`personnel-open-${d.id}`}
                  onClick={() => setSelectedId(d.id)}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    textAlign: 'left',
                    fontWeight: 600,
                    fontSize: 14.5,
                    color: 'var(--primary)',
                    cursor: 'pointer',
                  }}
                >
                  {d.user.fullName}
                </button>
                <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                  {isOwner ? (d.user.phone ?? t('fleet.noPhone')) : null}
                  {/* Hiç girmeyen için tek gösterge aşağıdaki rozettir (tekrar yok). */}
                  {isOwner && d.user.lastLoginAt
                    ? ` · ${formatDateTime(d.user.lastLoginAt)}`
                    : d.neverLoggedIn
                      ? null
                      : `${isOwner ? ' · ' : ''}${t(
                          d.loggedInWithin7Days
                            ? 'personnel.loggedIn7Days'
                            : 'personnel.notLoggedIn7Days',
                        )}`}
                </span>
                {d.openTrip || d.neverLoggedIn ? (
                  <span style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {d.openTrip ? (
                      <span
                        data-testid={`personnel-open-trip-${d.id}`}
                        style={{
                          fontSize: 11.5,
                          fontWeight: 600,
                          padding: '2px 8px',
                          borderRadius: 'var(--r-pill)',
                          background: 'var(--bg-success-soft)',
                          color: 'var(--primary)',
                        }}
                      >
                        {t('personnel.openTripBadge', { plate: d.openTrip.plate })}
                      </span>
                    ) : null}
                    {d.neverLoggedIn && !isOwnerProfile(d) ? (
                      <span
                        data-testid={`personnel-never-logged-in-${d.id}`}
                        style={{
                          fontSize: 11.5,
                          fontWeight: 600,
                          color: 'var(--warning)',
                          padding: '2px 8px',
                          borderRadius: 'var(--r-pill)',
                          background: 'var(--bg-warning-soft)',
                        }}
                      >
                        {t('personnel.neverLoggedInBadge')}
                      </span>
                    ) : null}
                  </span>
                ) : null}
                {isOwnerProfile(d) ? (
                  <span
                    data-testid={`personnel-owner-badge-${d.id}`}
                    style={{ fontSize: 11.5, color: 'var(--text-secondary)', fontWeight: 600 }}
                  >
                    {t('personnel.ownerProfileBadge')}
                  </span>
                ) : null}
                {isOwner && !isOwnerProfile(d) && d.isActive && !d.user.phone ? (
                  <>
                    <button
                      type="button"
                      data-testid={`personnel-phone-missing-${d.id}`}
                      onClick={() => setPhoneEdit({ id: d.id, value: '', error: null })}
                      style={phoneMissingBadgeStyle()}
                    >
                      {t('personnel.phoneMissingBadge')}
                    </button>
                    {phoneEdit?.id !== d.id ? (
                      <button
                        type="button"
                        className="btn"
                        data-testid={`personnel-phone-add-${d.id}`}
                        onClick={() => setPhoneEdit({ id: d.id, value: '', error: null })}
                        style={{ height: 34, alignSelf: 'flex-start' }}
                      >
                        {t('personnel.addPhone')}
                      </button>
                    ) : null}
                    {phoneEdit?.id === d.id ? (
                      <PhoneAddEditor
                        id={d.id}
                        value={phoneEdit.value}
                        error={phoneEdit.error}
                        pending={update.isPending}
                        onChange={(value) => setPhoneEdit({ ...phoneEdit, value, error: null })}
                        onSave={() => savePhone(d)}
                        onCancel={() => setPhoneEdit(null)}
                      />
                    ) : null}
                  </>
                ) : null}
              </span>
              <VehiclePicker
                driver={d}
                vehicles={vehicles}
                onPick={(vehicleId) =>
                  assignVehicle.mutate(
                    { id: d.id, vehicleId },
                    {
                      onSuccess: () =>
                        toast(
                          vehicleId
                            ? t('fleet.vehicleAssigned', {
                                name: d.user.fullName,
                                plate: vehicles.find((v) => v.id === vehicleId)?.plate ?? '',
                              })
                            : t('fleet.vehicleCleared', { name: d.user.fullName }),
                        ),
                      onError: (e) => setListError(personnelErrorMessage(e)),
                    },
                  )
                }
              />
              {isOwnerProfile(d) ? null : (
                <button
                  className="btn"
                  style={{ height: 38 }}
                  disabled={resetPin.isPending}
                  onClick={() => setConfirm({ kind: 'pin', driver: d })}
                >
                  {t('fleet.resetPin')}
                </button>
              )}
              {d.isActive ? (
                <button
                  className="btn"
                  style={{ height: 38 }}
                  onClick={() => setConfirm({ kind: 'retire', driver: d })}
                >
                  {t('fleet.retire')}
                </button>
              ) : (
                <>
                  <span style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
                    {t('fleet.retired')}
                  </span>
                  <button
                    className="btn"
                    style={{ height: 38 }}
                    disabled={update.isPending}
                    onClick={() =>
                      update.mutate(
                        { id: d.id, isActive: true },
                        {
                          onSuccess: () => {
                            setListError(null);
                            toast(t('fleet.driverReactivated', { name: d.user.fullName }));
                          },
                          onError: (e) => setListError(personnelErrorMessage(e)),
                        },
                      )
                    }
                  >
                    {t('fleet.reactivate')}
                  </button>
                </>
              )}
            </div>
          ))}
        </div>
      </section>

      {selected ? (
        // key: başka personele geçildiğinde Bilgiler formu (yerel state) yeni personelle sıfırlanır;
        // aksi hâlde A'nın adı/telefonu B'nin id'sine PATCH'lenirdi.
        <PersonDetail
          key={selected.id}
          driver={selected}
          vehicles={vehicles}
          canSeePhone={isOwner}
          onClose={() => setSelectedId(null)}
        />
      ) : null}

      {confirm ? (
        <ConfirmDialog
          title={confirm.kind === 'pin' ? t('fleet.resetPin') : t('fleet.retire')}
          body={
            confirm.kind === 'pin'
              ? t('personnel.resetPinConfirm', { name: confirm.driver.user.fullName })
              : t('personnel.retireConfirm', { name: confirm.driver.user.fullName })
          }
          confirmLabel={confirm.kind === 'pin' ? t('fleet.resetPin') : t('fleet.retire')}
          tone="danger"
          pending={resetPin.isPending || deactivate.isPending}
          onCancel={() => setConfirm(null)}
          onConfirm={() => {
            const d = confirm.driver;
            // Hata, liste hatası alanında gösterilir: onay penceresi kapanmış olur.
            if (confirm.kind === 'pin') {
              resetPin.mutate(d.id, {
                onSuccess: (r) => {
                  setListError(null);
                  setConfirm(null);
                  setPinCard({ name: d.user.fullName, pin: r.pin });
                },
                onError: (e) => {
                  setConfirm(null);
                  setListError(personnelErrorMessage(e));
                },
              });
            } else {
              deactivate.mutate(d.id, {
                onSuccess: () => {
                  setListError(null);
                  setConfirm(null);
                  toast(t('fleet.driverRetired', { name: d.user.fullName }));
                },
                onError: (e) => {
                  setConfirm(null);
                  setListError(personnelErrorMessage(e));
                },
              });
            }
          }}
        />
      ) : null}
    </div>
  );
}

/** "Telefon yok" rozeti (yalnız OWNER görür; tıklayınca telefon kutusu açılır). */
function phoneMissingBadgeStyle(): React.CSSProperties {
  return {
    fontSize: 11.5,
    fontWeight: 600,
    color: 'var(--warning)',
    alignSelf: 'flex-start',
    padding: '2px 8px',
    borderRadius: 'var(--r-pill)',
    background: 'var(--bg-warning-soft)',
    border: 'none',
    cursor: 'pointer',
  };
}

/** Telefonu olmayan satırda inline telefon kutusu. Kaydet/Vazgeç mantığı üst bileşende. */
function PhoneAddEditor({
  id,
  value,
  error,
  pending,
  onChange,
  onSave,
  onCancel,
}: {
  id: string;
  value: string;
  error: string | null;
  pending: boolean;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
}) {
  return (
    <div
      data-testid={`personnel-phone-editor-${id}`}
      style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 8 }}
    >
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        inputMode="tel"
        aria-label={t('personnel.infoPhone')}
        aria-required="true"
        placeholder={t('fleet.phonePlaceholder')}
        data-testid={`personnel-phone-add-input-${id}`}
        style={{ height: 38, width: 170 }}
      />
      <button
        className="btn btn-primary"
        style={{ height: 38 }}
        disabled={pending}
        data-testid={`personnel-phone-add-save-${id}`}
        onClick={onSave}
      >
        {t('common.save')}
      </button>
      <button
        className="btn"
        style={{ height: 38 }}
        data-testid={`personnel-phone-add-cancel-${id}`}
        onClick={onCancel}
      >
        {t('common.cancel')}
      </button>
      {error ? (
        <p
          role="alert"
          data-testid={`personnel-phone-add-error-${id}`}
          style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0, flexBasis: '100%' }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/** Kişi detayı: Bilgiler · Defter · Cihazlar. */
function PersonDetail({
  driver,
  vehicles,
  canSeePhone,
  onClose,
}: {
  driver: PersonnelDriver;
  vehicles: Vehicle[];
  canSeePhone: boolean;
  onClose: () => void;
}) {
  const [tab, setTab] = useState<'info' | 'ledger' | 'performance' | 'devices'>('info');
  const owner = isOwnerProfile(driver);

  return (
    <section
      className="card"
      data-testid="personnel-detail"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 14 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <h2 className="h2" style={{ margin: 0 }}>
          {driver.user.fullName}
        </h2>
        <span style={{ flex: 1 }} />
        <button className="btn no-print" style={{ height: 34 }} onClick={onClose}>
          {t('personnel.closeDetail')}
        </button>
      </div>

      <div
        className="no-print"
        style={{ display: 'flex', gap: 8 }}
        role="tablist"
        aria-label={t('personnel.detailTitle')}
      >
        <TabButton
          active={tab === 'info'}
          onClick={() => setTab('info')}
          testId="personnel-tab-info"
        >
          {t('personnel.tabInfo')}
        </TabButton>
        <TabButton
          active={tab === 'ledger'}
          onClick={() => setTab('ledger')}
          testId="personnel-tab-ledger"
        >
          {t('personnel.tabLedger')}
        </TabButton>
        {owner ? null : (
          <TabButton
            active={tab === 'performance'}
            onClick={() => setTab('performance')}
            testId="personnel-tab-perf"
          >
            {t('personnel.tabPerf')}
          </TabButton>
        )}
        <TabButton
          active={tab === 'devices'}
          onClick={() => setTab('devices')}
          testId="personnel-tab-devices"
        >
          {t('personnel.tabDevices')}
        </TabButton>
      </div>

      {tab === 'info' ? (
        <div className="no-print">
          <InfoTab driver={driver} vehicles={vehicles} owner={owner} canSeePhone={canSeePhone} />
        </div>
      ) : null}
      {tab === 'ledger' ? <LedgerTab driver={driver} /> : null}
      {tab === 'performance' && !owner ? (
        <div className="no-print">
          <PersonPerformance driverId={driver.id} />
        </div>
      ) : null}
      {tab === 'devices' ? (
        <div className="no-print">
          <DevicesTab driver={driver} owner={owner} />
        </div>
      ) : null}
    </section>
  );
}

/** "Bilgiler" sekmesi: ad, telefon, araç zimmeti. Patron profilinde ad/telefon kilitlidir. */
function InfoTab({
  driver,
  vehicles,
  owner,
  canSeePhone,
}: {
  driver: PersonnelDriver;
  vehicles: Vehicle[];
  owner: boolean;
  /** MANAGER telefonu göremez: alan formda yok, PATCH'e de konmaz (sunucu telefonu korur). */
  canSeePhone: boolean;
}) {
  const toast = useToast();
  const { update, assignVehicle } = usePersonnelMutations();
  const [fullName, setFullName] = useState(driver.user.fullName);
  const [phone, setPhone] = useState(driver.user.phone ?? '');
  const [vehicleId, setVehicleId] = useState(driver.vehicle?.id ?? '');
  const [error, setError] = useState<string | null>(null);
  /** PATCH + POST zinciri boyunca Kaydet kapalı. */
  const [saving, setSaving] = useState(false);

  // Satır içi telefon eklenince (liste tazelenir) açık detayın telefon alanı güncellenir.
  // Kullanıcı o an kendi değerini yazdıysa (kirli) ezilmez.
  const serverPhone = driver.user.phone ?? '';
  const [seenPhone, setSeenPhone] = useState(serverPhone);
  useEffect(() => {
    if (serverPhone === seenPhone) return;
    setSeenPhone(serverPhone);
    setPhone((cur) => (cur === seenPhone ? serverPhone : cur));
  }, [serverPhone, seenPhone]);

  // Zimmet de satırdan değişebilir: sunucu değeri değişince kirli değilse seçim senkronlanır.
  // Aksi hâlde Kaydet bayat seçimi "değişiklik" sayıp zimmeti geri ezerdi.
  const serverVehicle = driver.vehicle?.id ?? '';
  const [seenVehicle, setSeenVehicle] = useState(serverVehicle);
  useEffect(() => {
    if (serverVehicle === seenVehicle) return;
    setSeenVehicle(serverVehicle);
    setVehicleId((cur) => (cur === seenVehicle ? serverVehicle : cur));
  }, [serverVehicle, seenVehicle]);

  const phoneMissing = !owner && canSeePhone && phone.trim() === '';

  /** Kaydet: zincir boyunca `saving` açık; çift tıklama ikinci zinciri başlatmaz. */
  async function save(): Promise<void> {
    if (saving) return;
    setSaving(true);
    try {
      await saveChain();
    } finally {
      setSaving(false);
    }
  }

  /**
   * Detay kaydı zinciri: PATCH (ad/telefon) → zimmet değiştiyse POST /vehicle → toast.
   * `mutateAsync` kullanılır: mutate-seviyesi callback'ler bileşen değişince çağrılmaz.
   * PATCH düşerse POST gitmez. Zimmet PATCH gövdesinde hiç yer almaz (sunucu null'ı reddeder).
   */
  async function saveChain(): Promise<void> {
    const vehicleChanged = vehicleId !== (driver.vehicle?.id ?? '');
    const newVehicle = vehicleId === '' ? null : vehicleId;
    if (!owner) {
      if (fullName.trim().length < 2) return;
      if (canSeePhone && phone.trim() === '') {
        setError(t('personnel.phoneRequiredError'));
        return;
      }
    }
    setError(null);
    if (!owner) {
      try {
        await update.mutateAsync({
          id: driver.id,
          fullName: fullName.trim(),
          ...(canSeePhone ? { phone: phone.trim() } : {}),
        });
      } catch (e) {
        setError(personnelErrorMessage(e));
        return;
      }
    }
    if (vehicleChanged) {
      try {
        await assignVehicle.mutateAsync({ id: driver.id, vehicleId: newVehicle });
      } catch (e) {
        // Ad (ve OWNER ise telefon) yazıldı; yalnız zimmet yazılamadı — bunu açıkça söyle.
        setError(
          owner
            ? personnelErrorMessage(e)
            : t(canSeePhone ? 'personnel.partialSaveError' : 'personnel.partialSaveErrorManager', {
                error: personnelErrorMessage(e),
              }),
        );
        return;
      }
    }
    toast(t('personnel.infoSaved'));
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      {owner ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          {t('personnel.infoOwnerProfileNote')}
        </p>
      ) : (
        <div
          className="form-grid"
          style={{ '--form-cols': '1fr 1fr', '--form-gap': '10px' } as React.CSSProperties}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
              {t('personnel.infoFullName')}
            </span>
            <input
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              data-testid="personnel-info-name"
              style={{ height: 44 }}
            />
          </label>
          {canSeePhone ? (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <span
                style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}
                data-testid="personnel-info-phone-required-mark"
              >
                {t('personnel.phoneLabel')}
              </span>
              <input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                inputMode="tel"
                aria-required="true"
                data-testid="personnel-info-phone"
                style={{ height: 44 }}
              />
            </label>
          ) : null}
        </div>
      )}

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, maxWidth: 320 }}>
        <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
          {t('personnel.infoVehicle')}
        </span>
        <select
          value={vehicleId}
          onChange={(e) => setVehicleId(e.target.value)}
          data-testid="personnel-info-vehicle"
          style={{ height: 44 }}
        >
          <option value="">{t('fleet.noVehicleYet')}</option>
          {vehicles.map((v) => (
            <option key={v.id} value={v.id}>
              {v.plate}
              {v.label ? ` · ${v.label}` : ''}
            </option>
          ))}
        </select>
      </label>

      {owner ? null : <ChannelMapping driver={driver} />}

      {phoneMissing ? (
        <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0 }}>
          {t('personnel.phoneRequiredError')}
        </p>
      ) : null}
      {error ? (
        <p
          data-testid="personnel-detail-error"
          style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}
        >
          {error}
        </p>
      ) : null}

      <div>
        <button
          className="btn btn-primary"
          data-testid="personnel-info-save"
          disabled={saving || (!owner && (phoneMissing || fullName.trim().length < 2))}
          onClick={save}
        >
          {t('common.save')}
        </button>
      </div>
    </div>
  );
}

/**
 * Kanal eşlemesi — Abant çalışan numarası.
 *
 * Panel Atama ekranıyla AYNI uç ve AYNI veri (`ChannelConnection.config.driverMap`): okuma
 * `GET /api/panel-assign?source=ABANT_PANEL`, yazma `PUT /api/panel-assign/driver-map`. İkinci bir
 * kaynak yaratılmaz; burada değişen orada da değişir. Yazma yalnız patrondur (sunucu da öyle).
 * Bir kişiye birden çok numara eşli olabilir: burada ilki düzenlenir, ötekiler yalnız gösterilir
 * ve kaydedilirken dokunulmaz.
 */
function ChannelMapping({ driver }: { driver: PersonnelDriver }) {
  const toast = useToast();
  const me = useMe();
  const isOwner = me.data?.role === 'OWNER';
  const q = usePanelAssign('ABANT_PANEL');
  const save = useSaveDriverMap();
  const [picked, setPicked] = useState<string | null>(null);

  const labels = q.data?.labels ?? [];
  const mine = labels.filter((l) => l.driver?.id === driver.id).map((l) => l.label);
  const serverLabel = mine[0] ?? '';
  const value = picked ?? serverLabel;
  const dirty = value !== serverLabel;

  async function submit(): Promise<void> {
    const map: Record<string, string | null> = {};
    if (serverLabel !== '') map[serverLabel] = null;
    if (value !== '') map[value] = driver.id;
    try {
      const result = await save.mutateAsync({ source: 'ABANT_PANEL', map });
      setPicked(null);
      toast(
        result.rematchFailed ? t('personnel.channelRematchFailed') : t('personnel.channelSaved'),
        result.rematchFailed ? 'danger' : undefined,
      );
    } catch (e) {
      toast(personnelErrorMessage(e), 'danger');
    }
  }

  return (
    <div
      data-testid="personnel-channel-mapping"
      style={{ display: 'flex', flexDirection: 'column', gap: 6, maxWidth: 420 }}
    >
      <strong style={{ fontSize: 13 }}>{t('personnel.channelTitle')}</strong>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
        {t('personnel.channelIntro')}
      </p>
      {q.isError ? (
        <p role="alert" style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>
          {t('personnel.channelLoadError')}
        </p>
      ) : (
        <>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
              {t('personnel.channelAbant')}
            </span>
            <select
              value={value}
              disabled={!isOwner || q.isLoading || save.isPending}
              onChange={(e) => setPicked(e.target.value)}
              data-testid="personnel-channel-abant"
              style={{ height: 44 }}
            >
              <option value="">{t('personnel.channelNone')}</option>
              {labels.map((l) => {
                const takenBy = l.driver && l.driver.id !== driver.id ? l.driver.name : null;
                const text = l.displayName ? `${l.label} · ${l.displayName}` : l.label;
                return (
                  <option key={l.label} value={l.label} disabled={takenBy !== null}>
                    {takenBy !== null
                      ? t('personnel.channelTakenBy', { label: text, ad: takenBy })
                      : text}
                  </option>
                );
              })}
            </select>
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
              {t('personnel.channelAbantHint')}
            </span>
          </label>
          {q.data && labels.length === 0 ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('personnel.channelNoLabels')}
            </p>
          ) : null}
          {mine.length > 1 ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
              {mine.slice(1).join(', ')}
            </p>
          ) : null}
          {!isOwner ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('personnel.channelOwnerOnly')}
            </p>
          ) : (
            <div>
              <button
                className="btn"
                data-testid="personnel-channel-save"
                disabled={!dirty || save.isPending}
                onClick={() => void submit()}
              >
                {t('personnel.channelSave')}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

/**
 * "Defter" sekmesi — personel defteri (`GET /api/reports/driver-ledger`).
 *
 * Rakamlar SUNUCUDAN gelir; panel aralık toplamını da, gün toplamını da hesaplamaz
 * (kural 1 ve 4). Para string olarak gelir, yalnız biçimlenir.
 */
function LedgerTab({ driver }: { driver: PersonnelDriver }) {
  const today = istanbulDayString(new Date());
  const [from, setFrom] = useState(shiftIsoDay(today, -6));
  const [to, setTo] = useState(today);
  const [day, setDay] = useState<string | null>(null);

  const tooWide = from !== '' && to !== '' && dayCount(from, to) > LEDGER_MAX_DAYS;
  const range = usePersonnelLedger(driver.id, tooWide ? '' : from, tooWide ? '' : to);
  const dayQ = usePersonnelLedgerDay(driver.id, day);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <h2
        className="h2 print-only"
        data-testid="personnel-ledger-print-title"
        style={{ margin: 0 }}
      >
        {t('personnel.printTitle', {
          name: driver.user.fullName,
          from: printDate(from),
          to: printDate(to),
        })}
      </h2>
      <div
        className="form-grid no-print"
        style={{ '--form-cols': '160px 160px', '--form-gap': '10px' } as React.CSSProperties}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {t('personnel.ledgerFrom')}
          </span>
          <input
            type="date"
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            data-testid="personnel-ledger-from"
            style={{ height: 44 }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {t('personnel.ledgerTo')}
          </span>
          <input
            type="date"
            value={to}
            onChange={(e) => setTo(e.target.value)}
            data-testid="personnel-ledger-to"
            style={{ height: 44 }}
          />
        </label>
      </div>

      {tooWide ? (
        <p
          className="no-print"
          data-testid="personnel-ledger-range-error"
          style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}
        >
          {t('personnel.ledgerRangeError')}
        </p>
      ) : null}

      {range.isError ? (
        <p
          data-testid="personnel-ledger-error"
          style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}
        >
          {personnelErrorMessage(range.error)}
        </p>
      ) : null}

      {range.data ? <LedgerTotals range={range.data} /> : null}

      {range.data && range.data.days.length === 0 ? (
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
          {t('personnel.ledgerEmpty')}
        </p>
      ) : null}

      {range.data && range.data.days.length > 0 ? (
        <div style={{ overflowX: 'auto' }}>
          <table
            style={{ width: '100%', borderCollapse: 'collapse' }}
            data-testid="personnel-ledger-days"
          >
            <thead>
              <tr>
                {[
                  'personnel.ledgerDayTitle',
                  'personnel.ledgerTrips',
                  'personnel.ledgerDeliveries',
                  'pay.cash',
                  'pay.card',
                  'pay.online',
                  'pay.credit',
                  'personnel.ledgerFloatClosing',
                ].map((key) => (
                  <th
                    key={key}
                    style={{
                      textAlign: key === 'personnel.ledgerDayTitle' ? 'left' : 'right',
                      padding: '8px 10px',
                      fontSize: 12,
                      color: 'var(--text-secondary)',
                      borderBottom: '1px solid var(--border)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {t(key as Parameters<typeof t>[0])}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {range.data.days.map((row) => (
                <tr
                  key={row.date}
                  data-testid={`personnel-ledger-day-${row.date}`}
                  onClick={() => setDay(row.date)}
                  style={{
                    cursor: 'pointer',
                    borderTop: '1px solid var(--border)',
                    background: day === row.date ? 'var(--primary-bg)' : undefined,
                  }}
                >
                  <td style={{ padding: '8px 10px', fontSize: 13 }}>{row.date}</td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {row.tripCount}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {row.deliveredCount}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {formatMoney(row.collected.cash)}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {formatMoney(row.collected.card)}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {formatMoney(row.collected.online)}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {formatMoney(row.collected.credit)}
                  </td>
                  <td style={{ padding: '8px 10px', fontSize: 13, textAlign: 'right' }}>
                    {formatMoney(row.driverFloat.closing)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {day ? (
        <div className="no-print">
          <LedgerDayDetail
            day={dayQ.data}
            error={dayQ.isError ? personnelErrorMessage(dayQ.error) : null}
            onClose={() => setDay(null)}
          />
        </div>
      ) : null}
    </div>
  );
}

/** Aralık toplamı — sunucunun `totals` alanı olduğu gibi gösterilir. */
function LedgerTotals({ range }: { range: PersonnelLedgerRange }) {
  const totals = range.totals;
  const rows: Array<[string, string]> = [
    [t('personnel.ledgerTrips'), String(totals.tripCount)],
    [t('personnel.ledgerDeliveries'), String(totals.deliveredCount)],
    [t('pay.cash'), formatMoney(totals.collected.cash)],
    [t('pay.card'), formatMoney(totals.collected.card)],
    [t('pay.online'), formatMoney(totals.collected.online)],
    [t('pay.credit'), formatMoney(totals.collected.credit)],
    [t('personnel.ledgerDeposit'), formatMoney(totals.depositCollected)],
  ];
  return (
    <div
      className="card"
      data-testid="personnel-ledger-totals"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 6 }}
    >
      <strong style={{ fontSize: 12.5, color: 'var(--text-secondary)' }}>
        {t('personnel.ledgerTotal')}
      </strong>
      {rows.map(([label, value]) => (
        <div
          key={label}
          style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13.5 }}
        >
          <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
          <span style={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>{value}</span>
        </div>
      ))}
    </div>
  );
}

/** Tek gün detayı — seferler ve teslimatlar. */
function LedgerDayDetail({
  day,
  error,
  onClose,
}: {
  day: PersonnelLedgerDay | undefined;
  error: string | null;
  onClose: () => void;
}) {
  return (
    <div
      className="card"
      data-testid="personnel-ledger-day-detail"
      style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 10 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 13.5 }}>{t('personnel.ledgerDayTitle')}</strong>
        <span style={{ flex: 1 }} />
        <button className="btn" style={{ height: 32 }} onClick={onClose}>
          {t('common.close')}
        </button>
      </div>

      {error ? <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>{error}</p> : null}

      {day ? (
        <>
          <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('personnel.ledgerTrips')}: {day.summary.tripCount} ·{' '}
            {t('personnel.ledgerDeliveries')}: {day.summary.deliveredCount} · {t('pay.cash')}:{' '}
            {formatMoney(day.summary.collected.cash)} · {t('pay.card')}:{' '}
            {formatMoney(day.summary.collected.card)} · {t('pay.online')}:{' '}
            {formatMoney(day.summary.collected.online)} · {t('pay.credit')}:{' '}
            {formatMoney(day.summary.collected.credit)} · {t('personnel.ledgerFloatClosing')}:{' '}
            {formatMoney(day.summary.driverFloat.closing)}
          </div>

          {day.deliveries.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
              {t('personnel.ledgerNoDeliveries')}
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    {[
                      'personnel.ledgerCustomer',
                      'personnel.ledgerPayment',
                      'personnel.ledgerCollected',
                    ].map((key) => (
                      <th
                        key={key}
                        style={{
                          textAlign: key === 'personnel.ledgerCustomer' ? 'left' : 'right',
                          padding: '6px 8px',
                          fontSize: 12,
                          color: 'var(--text-secondary)',
                          borderBottom: '1px solid var(--border)',
                        }}
                      >
                        {t(key as Parameters<typeof t>[0])}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {day.deliveries.map((d) => (
                    <tr key={d.id} style={{ borderTop: '1px solid var(--border)' }}>
                      <td style={{ padding: '6px 8px', fontSize: 13 }}>
                        {d.customer?.name ?? '—'}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: 13, textAlign: 'right' }}>
                        {t(`paymentType.${d.paymentType}` as Parameters<typeof t>[0])}
                      </td>
                      <td style={{ padding: '6px 8px', fontSize: 13, textAlign: 'right' }}>
                        {formatMoney(d.amount)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

/**
 * "Cihazlar" sekmesi — personelin telefonla giriş yaptığı cihazlar.
 *
 * Liste SUNUCUDA süzgeçlenir (`GET /api/driver-devices?driverId=`): başka kiracının
 * personeli boş liste döner. Onay (6 haneli eşleştirme kodu) ve iptal de sunucudadır.
 */
function DevicesTab({ driver, owner }: { driver: PersonnelDriver; owner: boolean }) {
  const toast = useToast();
  const devices = useDriverDevices(driver.id);
  const approve = useApproveDevice();
  const revoke = useRevokeDevice();
  const unlock = useUnlockDevice();
  const viewerIsOwner = useMe().data?.role === 'OWNER';
  const [code, setCode] = useState('');
  const [error, setError] = useState<string | null>(null);

  const rows = devices.data ?? [];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div
        className="form-grid"
        style={
          {
            '--form-cols': '160px auto',
            '--form-gap': '10px',
            alignItems: 'end',
          } as React.CSSProperties
        }
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--text-secondary)' }}>
            {t('personnel.deviceApproveCode')}
          </span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            inputMode="numeric"
            data-testid="personnel-device-code"
            style={{ height: 44 }}
          />
        </label>
        <button
          className="btn btn-primary"
          data-testid="personnel-device-approve"
          disabled={owner || code.length !== 6 || approve.isPending}
          onClick={() =>
            approve.mutate(
              { pairingCode: code, driverId: driver.id },
              {
                onSuccess: () => {
                  setCode('');
                  toast(t('personnel.deviceApprovedToast'));
                },
                onError: (e) => setError(personnelErrorMessage(e)),
              },
            )
          }
        >
          {t('personnel.deviceApprove')}
        </button>
      </div>

      {owner ? (
        <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
          {t('device.ownerProfileNotAllowed')}
        </p>
      ) : null}
      {error ? <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>{error}</p> : null}

      <div
        className="card"
        style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 8 }}
      >
        {rows.length === 0 ? (
          <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
            {t('personnel.devicesEmpty')}
          </p>
        ) : null}

        {rows.map((device) => (
          <DeviceRow
            key={device.id}
            device={device}
            busy={revoke.isPending || unlock.isPending}
            canUnlock={viewerIsOwner}
            onUnlock={() =>
              unlock.mutate(device.id, {
                onSuccess: () => toast(t('personnel.deviceUnlockedToast')),
                onError: (e) => setError(personnelErrorMessage(e)),
              })
            }
            onRevoke={() =>
              revoke.mutate(device.id, {
                onSuccess: () => toast(t('personnel.deviceRevokedToast')),
                onError: (e) => setError(personnelErrorMessage(e)),
              })
            }
          />
        ))}
      </div>
    </div>
  );
}

function DeviceRow({
  device,
  busy,
  canUnlock,
  onUnlock,
  onRevoke,
}: {
  device: DriverDevice;
  busy: boolean;
  canUnlock: boolean;
  onUnlock: () => void;
  onRevoke: () => void;
}) {
  const lockMs = device.lockedUntil ? new Date(device.lockedUntil).getTime() - Date.now() : 0;
  const locked = lockMs > 0;
  const statusLabel =
    device.status === 'APPROVED'
      ? t('personnel.devicesApproved')
      : device.status === 'PENDING'
        ? t('personnel.devicesPending')
        : t('personnel.devicesRevoked');

  return (
    <div
      data-testid={`personnel-device-${device.id}`}
      style={{
        display: 'grid',
        gridTemplateColumns: '1fr 120px 150px auto',
        alignItems: 'center',
        gap: 10,
        borderTop: '1px solid var(--border)',
        padding: '8px 0',
        fontSize: 13,
      }}
    >
      <span style={{ fontWeight: 600 }}>{device.label ?? t('personnel.deviceNoLabel')}</span>
      <span style={{ color: 'var(--text-secondary)' }}>
        {statusLabel}
        {device.autoApproved ? (
          <span
            data-testid={`personnel-device-auto-${device.id}`}
            style={{ marginLeft: 6, fontSize: 11, color: 'var(--text-secondary)' }}
          >
            ({t('personnel.deviceAutoApproved')})
          </span>
        ) : null}
      </span>
      <span style={{ color: 'var(--text-secondary)' }}>
        {device.lastSeenAt ? formatDateTime(device.lastSeenAt) : t('personnel.deviceNeverSeen')}
      </span>
      <span style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
        {locked ? (
          <>
            <span
              data-testid={`personnel-device-locked-${device.id}`}
              style={{ color: 'var(--danger)' }}
            >
              {t('personnel.deviceLocked', { minutes: String(Math.ceil(lockMs / 60_000)) })}
            </span>
            {canUnlock ? (
              <button
                className="btn"
                style={{ height: 32 }}
                disabled={busy}
                data-testid={`personnel-device-unlock-${device.id}`}
                onClick={onUnlock}
              >
                {t('personnel.deviceUnlock')}
              </button>
            ) : null}
          </>
        ) : null}
        {device.status === 'REVOKED' ? null : (
          <button className="btn" style={{ height: 32 }} disabled={busy} onClick={onRevoke}>
            {t('personnel.deviceRevoke')}
          </button>
        )}
      </span>
    </div>
  );
}

/** Zimmet kutusu: açık seçim. */
function VehiclePicker({
  driver,
  vehicles,
  onPick,
}: {
  driver: PersonnelDriver;
  vehicles: Vehicle[];
  onPick: (vehicleId: string | null) => void;
}) {
  return (
    <select
      value={driver.vehicle?.id ?? ''}
      onChange={(e) => onPick(e.target.value === '' ? null : e.target.value)}
      style={{ height: 42, fontSize: 13 }}
      aria-label={t('fleet.assignedVehicle')}
    >
      <option value="">{t('fleet.noVehicle')}</option>
      {vehicles.map((v) => (
        <option key={v.id} value={v.id}>
          {v.plate}
        </option>
      ))}
    </select>
  );
}

/**
 * PIN kartı.
 *
 * PIN sunucuda üretilir ve düz metin olarak YALNIZ bir kez döner; kart kapanınca geri
 * okunamaz, yalnız sıfırlanır. Bu yüzden kart kendiliğinden kapanmaz.
 */
function PinCard({
  name,
  pin,
  onClose,
  onCopied,
}: {
  name: string;
  pin: string;
  onClose: () => void;
  onCopied: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <div
      className="card"
      data-testid="personnel-pin-card"
      style={{
        padding: 18,
        borderColor: 'var(--primary-light)',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <strong style={{ fontSize: 15 }}>{t('fleet.pinReady', { name })}</strong>
        <span style={{ flex: 1 }} />
        <button className="btn" style={{ height: 34, padding: '0 12px' }} onClick={onClose}>
          {t('common.close')}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        {pin.split('').map((digit, i) => (
          <span
            key={`${i}-${digit}`}
            style={{
              width: 44,
              height: 56,
              display: 'grid',
              placeItems: 'center',
              background: 'var(--primary-bg)',
              border: '1px solid var(--primary-light)',
              borderRadius: 'var(--r-row)',
              fontWeight: 700,
              fontSize: 24,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {digit}
          </span>
        ))}
      </div>
      <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.5, margin: 0 }}>
        {t('fleet.pinOnce')}
      </p>
      <div style={{ display: 'flex', gap: 8 }}>
        <button
          className="btn btn-primary"
          onClick={() => {
            void navigator.clipboard.writeText(pin).then(() => {
              setCopied(true);
              onCopied();
            });
          }}
        >
          {copied ? t('fleet.copied') : t('fleet.copy')}
        </button>
      </div>
    </div>
  );
}
